/**
 * Agentic mode: plans, publishing policy, the calendar, and the per-item gate / veto / verify state.
 *
 * Server contract (built in parallel; every GET degrades to an empty state when it 404s):
 *   GET  /api/workspaces/:id/plans, /plans/:planId
 *   POST /api/workspaces/:id/plans/generate {weeks, startDate}        -> {jobId}
 *   POST /api/workspaces/:id/plans/:planId/request-review             -> {jobId}
 *   POST /api/workspaces/:id/plans/:planId/approve | /archive
 *   PATCH /api/workspaces/:id/plans/:planId {title?, matrix?}
 *   GET/PUT /api/workspaces/:id/publish-policy
 *   GET  /api/workspaces/:id/calendar?from=YYYY-MM-DD&to=YYYY-MM-DD
 *   POST /api/workspaces/:id/content/:id/hold | /release | /gate
 *
 * Everything that reads a server payload is defensive: unknown shapes become "nothing to show", never a crash.
 * The pure helpers (no React, no network) come first; the query / mutation hooks are at the bottom.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  dayKey,
  deriveTitle,
  isPublished,
  platformKey,
  platformLabel,
  toDate,
  truncate,
  type StudioItem,
} from "./helpers";
import { errorMessage, platformConnected, useWorkspaceRole, wordpressConfigured } from "./use-studio";

// ── Channels ────────────────────────────────────────────────────────────────

export type PlanChannel = "blog" | "linkedin" | "x" | "facebook" | "email";
export type PolicyChannel = PlanChannel | "ad";

export const PLAN_CHANNELS: PlanChannel[] = ["blog", "linkedin", "x", "facebook", "email"];
export const POLICY_CHANNELS: PolicyChannel[] = ["blog", "linkedin", "x", "facebook", "email", "ad"];

export const CHANNEL_LABEL: Record<PolicyChannel, string> = {
  blog: "Blog",
  linkedin: "LinkedIn",
  x: "X",
  facebook: "Facebook",
  email: "Email",
  ad: "Ads",
};

export function channelLabel(channel: string): string {
  const key = String(channel || "").trim().toLowerCase();
  if (key === "twitter") return "X";
  if (key === "wordpress") return "Blog";
  return (CHANNEL_LABEL as Record<string, string>)[key] ?? (key ? key[0].toUpperCase() + key.slice(1) : "Other");
}

/** Which policy channel a content item publishes through, or null when the policy does not cover it (always manual). */
export function policyChannelOf(item: StudioItem): PolicyChannel | null {
  // Mirrors the server's channelOfItem() so the studio and the autopublisher agree on which policy applies.
  const p = platformKey(item);
  const ct = String(item.contentType || "").trim().toLowerCase();
  if (p === "blog" || p === "wordpress" || ct === "blog") return "blog";
  if (p === "linkedin") return "linkedin";
  if (p === "x" || p === "twitter") return "x";
  if (p === "facebook" || p === "meta") return "facebook";
  if (p === "email") return "email";
  if (p === "ad" || p === "ads") return "ad";
  return null;
}

/** true / false when the workspace says whether the channel is connected; null when it has no connection to speak of (email). */
export function channelConnected(workspace: any, channel: PolicyChannel): boolean | null {
  switch (channel) {
    case "blog": return wordpressConfigured(workspace);
    case "linkedin": return platformConnected(workspace, "linkedin");
    case "x": return platformConnected(workspace, "x");
    case "facebook": return platformConnected(workspace, "facebook");
    case "ad": return platformConnected(workspace, "meta") || platformConnected(workspace, "google");
    default: return null;
  }
}

export const PUBLISHING_SETTINGS_HREF = "/settings?tab=publishing";

/** What the channel connects to, in the words the Settings screen uses. */
export const CONNECTION_NAME: Partial<Record<PolicyChannel, string>> = {
  blog: "WordPress",
  linkedin: "LinkedIn",
  x: "X",
  facebook: "Facebook (Meta)",
  ad: "Meta or Google Ads",
};

export function connectionName(ch: PolicyChannel): string {
  return CONNECTION_NAME[ch] ?? CHANNEL_LABEL[ch];
}

// ── Publishing policy ───────────────────────────────────────────────────────

export type PolicyMode = "manual" | "veto" | "auto";
export interface ChannelPolicy {
  mode: PolicyMode;
  vetoHours?: number;
  /** Blog only: how many blog posts a week the plan may hold (1 to 6). Absent = the server did not send one. */
  blogsPerWeek?: number;
  /** Blog only: the share of blog slots that are NEW posts, 0 to 1 (the rest are updates of existing posts). 0.5 = half and half. */
  newUpdateRatio?: number;
  /** Blog only: a second reviewer must pass a piece before it can publish. Absent = the server default, which is on. */
  requireIndependentQa?: boolean;
  /** Facebook only: every Facebook post with an image is also posted to the Page's linked Instagram account. */
  alsoInstagram?: boolean;
}
/**
 * How fixes to posts that are already live (filed by the daily supervisor) are released.
 * Corrections stay manual until `manualUntil` whatever `mode` says (the handoff's two-week burn-in); after that, `mode` applies.
 */
export interface CorrectionsPolicy {
  mode: PolicyMode;
  vetoHours?: number;
  manualUntil?: string | null;
  /** What applies right now (read-only, sent by the server): corrections stay manual until `manualUntil` whatever `mode` says. */
  effectiveMode?: PolicyMode;
}
export type PublishPolicy = Record<PolicyChannel, ChannelPolicy> & { corrections?: CorrectionsPolicy };

export const DEFAULT_VETO_HOURS = 12;
/** What the server stores when the owner has not chosen: one blog post a day, Monday to Saturday, half of them updates. */
export const DEFAULT_BLOGS_PER_WEEK = 6;
export const DEFAULT_NEW_SHARE = 0.5;
export const MIN_BLOGS_PER_WEEK = 1;
export const MAX_BLOGS_PER_WEEK = 6;
/** The handoff advises approving corrections yourself for the first two weeks. */
export const CORRECTIONS_MANUAL_DAYS = 14;

export const MODE_LABEL: Record<PolicyMode, string> = {
  manual: "Manual",
  veto: "Veto window",
  auto: "Automatic",
};

export function defaultPolicy(): PublishPolicy {
  const out = {} as PublishPolicy;
  for (const ch of POLICY_CHANNELS) out[ch] = { mode: "manual" };
  return out;
}

/** A number from a JSON value that may be a number or a numeric string; NaN for anything else (null and "" are not zero). */
function jsonNumber(x: unknown): number {
  if (typeof x === "number") return x;
  if (typeof x === "string" && x.trim() !== "") return Number(x);
  return NaN;
}

function modeOf(x: unknown): PolicyMode {
  return x === "veto" || x === "auto" ? x : "manual";
}

/** The blog-only fields of a channel entry; a field the server did not send stays absent (so a save never invents one). */
function blogExtrasOf(v: Record<string, any>): Pick<ChannelPolicy, "blogsPerWeek" | "newUpdateRatio" | "requireIndependentQa"> {
  const out: Pick<ChannelPolicy, "blogsPerWeek" | "newUpdateRatio" | "requireIndependentQa"> = {};
  const perWeek = jsonNumber(v.blogsPerWeek);
  if (Number.isInteger(perWeek) && perWeek >= MIN_BLOGS_PER_WEEK && perWeek <= MAX_BLOGS_PER_WEEK) out.blogsPerWeek = perWeek;
  const ratio = jsonNumber(v.newUpdateRatio);
  if (Number.isFinite(ratio) && ratio >= 0 && ratio <= 1) out.newUpdateRatio = ratio;
  if (typeof v.requireIndependentQa === "boolean") out.requireIndependentQa = v.requireIndependentQa;
  return out;
}

function correctionsOf(v: unknown): CorrectionsPolicy | undefined {
  if (!v || typeof v !== "object") return undefined;
  const o = v as Record<string, any>;
  const hours = jsonNumber(o.vetoHours);
  const until = typeof o.manualUntil === "string" && toDate(o.manualUntil) ? o.manualUntil : null;
  const effective = o.effectiveMode === "manual" || o.effectiveMode === "veto" || o.effectiveMode === "auto" ? (o.effectiveMode as PolicyMode) : null;
  return {
    mode: modeOf(o.mode),
    ...(Number.isFinite(hours) && hours > 0 ? { vetoHours: hours } : {}),
    ...(until ? { manualUntil: until } : {}),
    ...(effective ? { effectiveMode: effective } : {}),
  };
}

export function normalizePolicy(raw: unknown): PublishPolicy {
  const out = defaultPolicy();
  if (!raw || typeof raw !== "object") return out;
  const src = ((raw as any).policy && typeof (raw as any).policy === "object" ? (raw as any).policy : raw) as Record<string, any>;
  for (const ch of POLICY_CHANNELS) {
    const v = src[ch];
    if (!v || typeof v !== "object") continue;
    const mode = modeOf(v.mode);
    const hours = Number(v.vetoHours);
    out[ch] = { mode, ...(Number.isFinite(hours) && hours > 0 ? { vetoHours: hours } : {}), ...(ch === "blog" ? blogExtrasOf(v) : {}), ...(ch === "facebook" && v.alsoInstagram === true ? { alsoInstagram: true } : {}) };
  }
  const corrections = correctionsOf(src.corrections);
  if (corrections) out.corrections = corrections;
  return out;
}

/** The blog cadence the policy states. A value the server never sent is null (so nothing is warned about), except the QA switch, which defaults to on. */
export interface BlogCadence {
  blogsPerWeek: number | null;
  /** Share of blog slots that are NEW posts (0 to 1). */
  newShare: number | null;
  requireIndependentQa: boolean;
}

export function blogCadenceOf(policy: PublishPolicy | null | undefined): BlogCadence {
  const b = policy?.blog;
  return { blogsPerWeek: b?.blogsPerWeek ?? null, newShare: b?.newUpdateRatio ?? null, requireIndependentQa: b?.requireIndependentQa !== false };
}

/** How the policy reads corrections when the server sent nothing: Manual, as the handoff advises. */
export function correctionsOfPolicy(policy: PublishPolicy | null | undefined): CorrectionsPolicy {
  return policy?.corrections ?? { mode: "manual" };
}

/**
 * The calendar day of a policy date. The server stores "manualUntil" as the end of a UTC day (a picked YYYY-MM-DD becomes
 * YYYY-MM-DDT23:59:59.999Z), so the day is the first ten characters, read as a local calendar day: reading the instant in local time
 * would show the next day east of UTC, and a date saved from the picker would not round-trip.
 */
export function policyDateOf(s: string | null | undefined): Date | null {
  if (!s) return null;
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? parseDayKey(s.slice(0, 10)) : toDate(s);
}

/** YYYY-MM-DD for a date input, from a stored "manualUntil". */
export function policyDayKey(s: string | null | undefined): string {
  return /^\d{4}-\d{2}-\d{2}/.test(s ?? "") ? String(s).slice(0, 10) : "";
}

/**
 * The one-phrase state of the corrections policy: "Manual", "Veto window (12 h)", "Automatic", or, while the owner's hold date is still ahead,
 * "Manual until Oct 19, then veto window (12 h)". The server's own `effectiveMode` wins when it sent one.
 */
export function describeCorrections(c: CorrectionsPolicy, now: number = Date.now()): string {
  if (c.mode === "manual") return "Manual";
  const after = c.mode === "auto" ? "Automatic" : `Veto window (${c.vetoHours ?? DEFAULT_VETO_HOURS} h)`;
  const day = policyDayKey(c.manualUntil);
  const holding = !!day && (c.effectiveMode ? c.effectiveMode === "manual" : day >= dayKey(new Date(now)));
  if (!holding) return after;
  const until = policyDateOf(day);
  return until ? `Manual until ${until.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, then ${after[0].toLowerCase()}${after.slice(1)}` : after;
}

/** What the server will do with an item's channel. A channel that is not connected counts as manual: nothing can go out by itself. */
export function policyModeFor(policy: PublishPolicy | null | undefined, workspace: any, item: StudioItem): PolicyMode {
  const ch = policyChannelOf(item);
  if (!ch || !policy) return "manual";
  const mode = policy[ch]?.mode ?? "manual";
  if (mode !== "manual" && channelConnected(workspace, ch) === false) return "manual";
  return mode;
}

/**
 * True when at least one channel really publishes by itself: its mode is Veto or Automatic AND it is connected
 * (a channel that is not connected can never publish by itself, so it behaves as Manual).
 */
export function automationActive(policy: PublishPolicy | null | undefined, workspace: any): boolean {
  if (!policy) return false;
  return POLICY_CHANNELS.some((ch) => policy[ch].mode !== "manual" && channelConnected(workspace, ch) !== false);
}

/** One sentence per mode, used on the confirm dialog and the plan tab. */
export function describePolicy(p: ChannelPolicy | undefined): string {
  const mode = p?.mode ?? "manual";
  if (mode === "auto") return "publishes automatically as soon as it passes the check";
  if (mode === "veto") return `publishes by itself ${p?.vetoHours ?? DEFAULT_VETO_HOURS} hours after it passes the check, unless you hold it`;
  return "waits for your approval";
}

// ── Gate, veto, verify (per content item) ───────────────────────────────────

export interface GateCheck {
  name: string;
  passed: boolean;
  detail?: string;
  /** false = a warning: it does not stop publishing even when it fails. */
  blocking?: boolean;
  /** The measurement could not be taken (a network failure): reported as "not measured", never as passed and never as a failure of the piece. */
  unverified?: boolean;
}
export interface GateResult {
  passed: boolean;
  checks: GateCheck[];
  ranAt: string | null;
  /** The deterministic checks passed and the independent QA review has not finished yet: the server will not schedule or release it until it does. */
  awaitingQa: boolean;
  /** When the independent QA review was ordered (only while awaitingQa). */
  qaRequestedAt: string | null;
  /** Fingerprint of the exact draft the checks ran on; the QA review carries the one it was filed for. */
  contentHash: string | null;
}

/** Checks as the server sends them (the pre-publish gate and the after-publish verification share one shape), tolerant of older spellings. */
export function normalizeChecks(raw: unknown): GateCheck[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c: any) => c && typeof c === "object")
    .map((c: any, i: number) => ({
      name: String(c.name ?? c.id ?? `check ${i + 1}`),
      passed: typeof c.passed === "boolean" ? c.passed : c.ok === true,
      detail: typeof c.detail === "string" && c.detail.trim() ? c.detail.trim() : typeof c.message === "string" && c.message.trim() ? c.message.trim() : undefined,
      blocking: c.blocking === false ? false : undefined,
      unverified: c.unverified === true ? true : undefined,
    }));
}

export function gateOf(item: StudioItem): GateResult | null {
  const g = item.gateResult as any;
  if (!g || typeof g !== "object") return null;
  const checks = normalizeChecks(g.checks);
  const passed = typeof g.passed === "boolean" ? g.passed : checks.length ? checks.every((c) => c.passed || c.blocking === false) : null;
  if (passed === null) return null;
  // Failed checks first: they are what the owner has to act on.
  const ordered = [...checks.filter((c) => !c.passed), ...checks.filter((c) => c.passed)];
  return {
    passed,
    checks: ordered,
    ranAt: typeof g.ranAt === "string" ? g.ranAt : null,
    awaitingQa: g.awaitingQa === true,
    qaRequestedAt: typeof g.qaRequestedAt === "string" ? g.qaRequestedAt : null,
    contentHash: typeof g.contentHash === "string" ? g.contentHash : null,
  };
}

export type VetoState = "none" | "pending" | "held" | "released";

export function vetoStateOf(item: StudioItem): VetoState {
  const v = String(item.vetoState || "").toLowerCase();
  return v === "pending" || v === "held" || v === "released" ? v : "none";
}

export function publishAfterOf(item: StudioItem): Date | null {
  return toDate(item.publishAfter);
}

export function isNeedsFix(item: StudioItem): boolean {
  return String(item.status || "").toUpperCase() === "NEEDS_FIX";
}

export type VerifyState = "verified" | "pulled_back" | "still_live";
export interface VerifyView {
  state: VerifyState;
  reason: string | null;
  checkedAt: string | null;
  /** Every check the server measured on the live page (about a dozen), each with its measured value in `detail`. */
  checks: GateCheck[];
  /** The live page could not be loaded at all, so nothing was judged and nothing was taken down. */
  inconclusive?: boolean;
  /** An update that failed the check and was put back to the version it replaced (the server says so; the client never assumes it). */
  restored?: boolean;
}

/** The post-publish check. A pulled-back post was taken down again because verification found a problem. */
export function verifyOf(item: StudioItem): VerifyView | null {
  const status = String(item.status || "").toUpperCase();
  const v = item.verifyResult as any;
  if (v === null || v === undefined || v === "") {
    return status === "PULLED_BACK" ? { state: "pulled_back", reason: null, checkedAt: null, checks: [] } : null;
  }
  if (typeof v === "boolean") return { state: v ? "verified" : "pulled_back", reason: null, checkedAt: null, checks: [] };
  if (typeof v === "string") {
    const s = v.toLowerCase();
    if (/pull|remov|unpublish|revert/.test(s)) return { state: "pulled_back", reason: v, checkedAt: null, checks: [] };
    if (/fail|error/.test(s)) return { state: "still_live", reason: v, checkedAt: null, checks: [] };
    return /verif|pass|ok|live/.test(s) ? { state: "verified", reason: null, checkedAt: null, checks: [] } : null;
  }
  if (typeof v !== "object") return null;

  const word = String(v.action ?? v.outcome ?? v.status ?? "").toLowerCase();
  const restored = v.restored === true || typeof v.restoredAt === "string" || /restor/.test(word);
  const pulled = restored || v.pulledBack === true || v.pulled_back === true || /pull|unpublish|remov|revert/.test(word) || status === "PULLED_BACK";
  const checks = normalizeChecks(v.checks);
  // Failed checks first, as in the pre-publish list: they are what the owner has to read.
  const orderedChecks = [...checks.filter((c) => !c.passed), ...checks.filter((c) => c.passed)];
  let passed: boolean | null = null;
  for (const k of ["passed", "verified", "ok", "success"]) if (typeof v[k] === "boolean") { passed = v[k]; break; }
  if (passed === null && /verif|pass|ok/.test(word) && !/not|fail/.test(word)) passed = true;
  if (passed === null && /fail/.test(word)) passed = false;

  let reason: string | null = null;
  for (const k of ["reason", "message", "detail", "error", "note"]) {
    if (typeof v[k] === "string" && v[k].trim()) { reason = v[k].trim(); break; }
  }
  if (!reason && Array.isArray(v.checks)) {
    const bad = v.checks
      .filter((c: any) => c && typeof c === "object" && (c.passed === false || c.ok === false))
      .map((c: any) => (typeof c.detail === "string" && c.detail.trim() ? `${humanize(c.name)}: ${c.detail.trim()}` : humanize(c.name)));
    if (bad.length) reason = bad.join("; ");
  }
  const checkedAt = typeof v.verifiedAt === "string" ? v.verifiedAt : typeof v.checkedAt === "string" ? v.checkedAt : typeof v.ranAt === "string" ? v.ranAt : null;
  if (pulled) return { state: "pulled_back", reason, checkedAt, checks: orderedChecks, ...(restored ? { restored: true } : {}) };
  if (passed === true) return { state: "verified", reason, checkedAt, checks: orderedChecks };
  if (passed === false) {
    // Could not load the page at all: the server leaves the post up and asks the owner to look.
    if (v.inconclusive === true) return { state: "still_live", reason, checkedAt, checks: orderedChecks, inconclusive: true };
    // A failed check makes the server take the post down. It keeps the publish date only when taking it down failed.
    const stillUp = v.pulledBack === false || v.pulled_back === false || isPublished(item);
    return { state: stillUp ? "still_live" : "pulled_back", reason, checkedAt, checks: orderedChecks };
  }
  return null;
}

function humanize(name: unknown): string {
  const s = String(name ?? "").replace(/[_-]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase().trim();
  return s ? s[0].toUpperCase() + s.slice(1) : "A check";
}

/** The publish error in words an owner can act on, plus where to fix it when it is a connection problem. */
export function plainPublishError(item: StudioItem, workspace: any): { text: string; raw: string; fixHref?: string; fixLabel?: string } | null {
  const raw = String(item.publishError || "").trim();
  if (!raw) return null;
  const ch = policyChannelOf(item);
  const name = ch ? connectionName(ch) : platformLabel(item);
  const expired = /expired|revoked|invalid[^.]*token|token[^.]*(invalid|expired)|reauth|reconnect/i.test(raw);
  const missing = /not connected|isn'?t connected|no (linked|connected|credentials|token|access)|missing[^.]*(token|credential)|credentials?[^.]*(not|missing)|unauthori[sz]ed|\b40[13]\b/i.test(raw);
  const notConnected = ch ? channelConnected(workspace, ch) === false : false;
  if (expired) {
    return { text: `${name}'s connection has expired. Reconnect it in Settings → Publishing.`, raw, fixHref: PUBLISHING_SETTINGS_HREF, fixLabel: `Reconnect ${name}` };
  }
  if (missing || notConnected) {
    return { text: `${name} isn't connected. Connect it in Settings → Publishing.`, raw, fixHref: PUBLISHING_SETTINGS_HREF, fixLabel: `Connect ${name}` };
  }
  return { text: `It couldn't be published: ${truncate(raw, 240)}`, raw };
}

// ── Countdown ───────────────────────────────────────────────────────────────

export function formatCountdown(ms: number): string {
  if (ms <= 0) return "any moment now";
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 1) return "under a minute";
  if (totalMin < 60) return `${totalMin}m`;
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours < 24) return mins ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
}

/** Re-renders the caller every `ms` so countdowns stay honest. */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

// ── Review inbox classification ─────────────────────────────────────────────

export type ReviewReason = "held" | "fix" | "approve";

/**
 * Why (if at all) an item needs a human. Items on a veto or automatic channel that are flowing through the
 * pipeline are not listed: they publish by themselves, and show on the Calendar / Ready tab instead.
 *  - held:    the owner held it.
 *  - fix:     marked NEEDS_FIX, or the pre-publish check failed on something the plan did not ask for (off-plan).
 *  - approve: the channel is on Manual (or the policy is unknown, which behaves as Manual), so each piece needs a yes;
 *             or an off-plan piece on a Veto / Automatic channel that has not been through the pre-publish check.
 */
export function reviewReasonOf(item: StudioItem, policy: PublishPolicy | null | undefined, workspace: any): ReviewReason | null {
  const approval = String(item.approvalStatus || "").toLowerCase();
  if (approval === "rejected" || isPublished(item)) return null;
  if (isNeedsFix(item)) return "fix";
  if (vetoStateOf(item) === "held") return "held";
  const pending = approval === "pending_approval" || approval === "pending" || approval === "";
  if (!pending) return null;
  if (policyModeFor(policy, workspace, item) === "manual") return "approve";
  // Veto / automatic channel. A piece that is part of the plan flows through the pipeline by itself (the team re-gates it, or the
  // server marks it NEEDS_FIX), so it is not listed. A piece that is NOT part of the plan has no automatic path to publication,
  // so it is listed: as a fix when its pre-publish check failed, and for approval when it has not been checked at all.
  if (item.planSlotId) return null;
  const gate = gateOf(item);
  if (gate && gate.passed) return null;
  return gate ? "fix" : "approve";
}

export interface ExplainerContext {
  /** The publishing policy loaded. When it did not, every channel behaves as Manual. */
  policyKnown: boolean;
  /** At least one channel is on Veto or Automatic. */
  automatic: boolean;
}

export const REVIEW_GROUPS: { id: ReviewReason; label: string; explainer: (ctx: ExplainerContext) => string }[] = [
  {
    id: "held",
    label: "On hold",
    explainer: () => "You held these. They stay here until you release them to publish, or reject them.",
  },
  {
    id: "fix",
    label: "Needs a fix",
    explainer: () => "The team marked these as needing a fix (for example a post that was pulled back after publishing), or the pre-publish check failed on something that wasn't in the plan. Edit or reject them.",
  },
  {
    id: "approve",
    label: "Waiting for your approval",
    explainer: ({ policyKnown, automatic }) =>
      !policyKnown
        ? "Open an item, read it, then approve or reject it. Nothing goes out until you approve it."
        : automatic
          ? "These are on a Manual channel, or weren't part of the plan and haven't passed the pre-publish check. Nothing goes out until you approve each piece."
          : "These channels are set to Manual, so nothing goes out until you approve each piece.",
  },
];

// ── Calendar ────────────────────────────────────────────────────────────────

export interface CalendarEntry {
  date: string;
  kind: "slot" | "item";
  channel: string;
  title: string;
  status: string;
  contentItemId?: number | null;
  slotId?: string | null;
  planId?: number | string | null;
  publishAfter?: string | null;
  vetoState?: string | null;
  verified?: boolean | null;
  publishedUrl?: string | null;
}

export type CalState =
  | "planned"
  | "ordered"
  | "drafted"
  | "scheduled"
  | "waiting"
  | "held"
  | "published"
  | "verified"
  | "pulled_back"
  | "needs_fix"
  | "failed"
  | "skipped";

export const CAL_STATE_LABEL: Record<CalState, string> = {
  planned: "Planned",
  ordered: "Being written",
  drafted: "Draft ready",
  scheduled: "Scheduled",
  waiting: "Waiting to publish",
  held: "On hold",
  published: "Published",
  verified: "Published, verified",
  pulled_back: "Pulled back",
  needs_fix: "Needs a fix",
  failed: "Couldn't post",
  skipped: "Skipped",
};

/** Outline = planned, dotted = being written, amber = waiting on the veto window, grey = held, green = published, red = problem. */
export const CAL_STATE_CLASS: Record<CalState, string> = {
  planned: "border border-solid border-foreground/40 bg-transparent text-foreground/85",
  ordered: "border border-dotted border-sky-400/70 bg-sky-500/5 text-sky-200",
  drafted: "border border-solid border-sky-500/30 bg-sky-500/15 text-sky-200",
  scheduled: "border border-solid border-blue-500/30 bg-blue-500/15 text-blue-200",
  waiting: "border border-solid border-amber-500/40 bg-amber-500/15 text-amber-200",
  held: "border border-solid border-zinc-500/40 bg-zinc-500/20 text-zinc-300",
  published: "border border-solid border-emerald-500/30 bg-emerald-500/15 text-emerald-200",
  verified: "border border-solid border-emerald-500/40 bg-emerald-500/20 text-emerald-100",
  pulled_back: "border border-solid border-red-500/40 bg-red-500/15 text-red-200",
  needs_fix: "border border-solid border-orange-500/40 bg-orange-500/15 text-orange-200",
  failed: "border border-solid border-red-500/40 bg-red-500/15 text-red-200",
  skipped: "border border-solid border-border bg-muted/20 text-muted-foreground line-through",
};

export function calStateOf(e: CalendarEntry): CalState {
  const veto = String(e.vetoState || "").toLowerCase();
  const s = String(e.status || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (s === "pulled_back" || s === "unpublished" || e.verified === false) return "pulled_back";
  if (s === "needs_fix") return "needs_fix";
  if (veto === "held") return "held";
  if (veto === "pending") return "waiting";
  if (s === "published" || (!!e.publishedUrl && !/fail|error/.test(s))) return e.verified === true ? "verified" : "published";
  if (/fail|error/.test(s)) return "failed";
  if (s === "skipped") return "skipped";
  if (s === "planned") return "planned";
  if (["ordered", "drafting", "queued", "claimed", "working", "in_progress", "writing"].includes(s)) return "ordered";
  if (s === "gated") return "waiting";
  if (s === "scheduled" || s === "approved" || s === "ready") return "scheduled";
  if (["drafted", "draft", "pending_approval", "pending", "needs_fix"].includes(s)) return "drafted";
  return e.kind === "slot" ? "planned" : "drafted";
}

/** Calendar entry for a content item, used when the calendar endpoint is not available yet. */
export function entryFromItem(item: StudioItem): CalendarEntry | null {
  if (String(item.approvalStatus || "").toLowerCase() === "rejected") return null;
  const when = toDate(item.publishAfter) ?? toDate(item.scheduledAt) ?? (isPublished(item) ? toDate(item.publishedAt) : null);
  if (!when) return null;
  const verify = verifyOf(item);
  return {
    date: dayKey(when),
    kind: "item",
    channel: policyChannelOf(item) ?? (platformKey(item) || "other"),
    title: deriveTitle(item),
    status: String(item.status || ""),
    contentItemId: item.id,
    slotId: item.planSlotId ?? null,
    planId: item.contentPlanId ?? null,
    publishAfter: item.publishAfter ? toDate(item.publishAfter)?.toISOString() ?? null : null,
    vetoState: item.vetoState ?? null,
    verified: verify ? verify.state === "verified" : null,
    publishedUrl: isPublished(item) ? item.blogUrl ?? null : null,
  };
}

/** The local day (YYYY-MM-DD) an entry sits on. Date-only strings are taken as written, never shifted by the time zone. */
export function entryDayKey(e: CalendarEntry): string {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(e.date || ""));
  if (m && !/T\d{2}:\d{2}/.test(String(e.date))) return m[1];
  const d = toDate(e.date);
  return d ? dayKey(d) : "";
}

export function parseDayKey(k: string): Date {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  return x;
}

/** Monday-to-Sunday weeks, because that is how a content plan is read. */
export function startOfWeek(d: Date): Date {
  const dow = (d.getDay() + 6) % 7;
  return addDays(new Date(d.getFullYear(), d.getMonth(), d.getDate()), -dow);
}

// ── Plans ───────────────────────────────────────────────────────────────────

export type PlanStatus = "draft" | "reviewed" | "approved" | "archived";
export type SlotStatus = "planned" | "ordered" | "drafted" | "gated" | "published" | "skipped";

export interface PlanSlot {
  slotId: string;
  date: string;
  channel: PlanChannel;
  topic: string;
  angle: string;
  targetQuery: string;
  kind: "new" | "update";
  updateOf?: string | number | null;
  status: SlotStatus;
  contentItemId?: number | null;
  jobId?: string | number | null;
  /** The slot exactly as the server sent it. It carries keys this screen does not know (why, bucket, sources ...), and they must survive a save. */
  raw?: Record<string, unknown>;
}

export interface PlanReview {
  by: string;
  at: string | null;
  verdict: string;
  notes: string[];
  suggestedChanges: string[];
}

export interface Plan {
  id: number | string;
  title: string;
  periodStart: string;
  periodEnd: string;
  status: PlanStatus;
  matrix: PlanSlot[];
  review: PlanReview | null;
  createdBy: string;
  approvedAt: string | null;
}

const PLAN_STATUSES: PlanStatus[] = ["draft", "reviewed", "approved", "archived"];
const SLOT_STATUSES: SlotStatus[] = ["planned", "ordered", "drafted", "gated", "published", "skipped"];

/** One line for a review note or suggested change, whatever shape the supervisor wrote it in. */
function lineOf(x: unknown): string {
  if (typeof x === "string") return x.trim();
  if (!x || typeof x !== "object") return "";
  const o = x as Record<string, unknown>;
  for (const k of ["text", "note", "change", "suggestion", "description", "message", "reason"]) {
    if (typeof o[k] === "string" && (o[k] as string).trim()) {
      const slot = typeof o.slotId === "string" && o.slotId ? `[${o.slotId}] ` : "";
      return `${slot}${(o[k] as string).trim()}`;
    }
  }
  return Object.entries(o)
    .filter(([, val]) => typeof val === "string" || typeof val === "number")
    .map(([key, val]) => `${key}: ${val}`)
    .join(", ");
}

function strList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(lineOf).filter(Boolean);
  if (typeof v === "string" && v.trim()) return v.split(/\r?\n/).map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim()).filter(Boolean);
  return [];
}

function normalizeSlot(raw: any, i: number): PlanSlot | null {
  if (!raw || typeof raw !== "object") return null;
  const ch = String(raw.channel || "").toLowerCase();
  const channel = (ch === "twitter" ? "x" : ch === "wordpress" ? "blog" : ch) as PlanChannel;
  const status = (SLOT_STATUSES as string[]).includes(raw.status) ? (raw.status as SlotStatus) : "planned";
  const date = typeof raw.date === "string" ? raw.date : toDate(raw.date) ? dayKey(toDate(raw.date)!) : "";
  if (!date) return null;
  return {
    slotId: String(raw.slotId ?? raw.id ?? `slot-${i}`),
    date,
    channel: PLAN_CHANNELS.includes(channel) ? channel : "blog",
    topic: String(raw.topic ?? raw.title ?? ""),
    angle: String(raw.angle ?? ""),
    targetQuery: String(raw.targetQuery ?? raw.target_query ?? ""),
    kind: raw.kind === "update" ? "update" : "new",
    updateOf: raw.updateOf ?? null,
    status,
    contentItemId: typeof raw.contentItemId === "number" ? raw.contentItemId : null,
    jobId: raw.jobId ?? null,
    raw,
  };
}

/**
 * Anything without a `matrix` array is not a content plan (the older "marketing plans" rows come back from the
 * same URL on servers that predate agentic mode), so it is dropped instead of being half-rendered.
 */
export function normalizePlan(raw: any): Plan | null {
  if (!raw || typeof raw !== "object" || !Array.isArray(raw.matrix) || raw.id === undefined || raw.id === null) return null;
  const status = (PLAN_STATUSES as string[]).includes(raw.status) ? (raw.status as PlanStatus) : "draft";
  const matrix = raw.matrix.map(normalizeSlot).filter(Boolean) as PlanSlot[];
  matrix.sort((a, b) => a.date.localeCompare(b.date) || a.channel.localeCompare(b.channel));
  const r = raw.review;
  const review: PlanReview | null =
    r && typeof r === "object"
      ? { by: String(r.by ?? "Supervisor"), at: typeof r.at === "string" ? r.at : null, verdict: String(r.verdict ?? ""), notes: strList(r.notes), suggestedChanges: strList(r.suggestedChanges) }
      : null;
  return {
    id: raw.id,
    title: String(raw.title || "Content plan"),
    periodStart: String(raw.periodStart ?? matrix[0]?.date ?? ""),
    periodEnd: String(raw.periodEnd ?? matrix[matrix.length - 1]?.date ?? ""),
    status,
    matrix,
    review,
    createdBy: String(raw.createdBy ?? ""),
    approvedAt: typeof raw.approvedAt === "string" ? raw.approvedAt : null,
  };
}

export function normalizePlans(json: unknown): Plan[] {
  const list = Array.isArray(json) ? json : json && typeof json === "object" && Array.isArray((json as any).plans) ? (json as any).plans : [];
  const plans = list.map(normalizePlan).filter(Boolean) as Plan[];
  return plans.sort((a, b) => Number(b.id) - Number(a.id) || b.periodStart.localeCompare(a.periodStart));
}

export const SLOT_STATUS_LABEL: Record<SlotStatus, string> = {
  planned: "Planned",
  ordered: "Being written",
  drafted: "Draft ready",
  gated: "Waiting to publish",
  published: "Published",
  skipped: "Skipped",
};

export const SLOT_STATUS_CLASS: Record<SlotStatus, string> = {
  planned: CAL_STATE_CLASS.planned,
  ordered: CAL_STATE_CLASS.ordered,
  drafted: CAL_STATE_CLASS.drafted,
  gated: CAL_STATE_CLASS.waiting,
  published: CAL_STATE_CLASS.published,
  skipped: CAL_STATE_CLASS.skipped,
};

/** Draper dispatches a slot automatically this many days before its date. */
export const ORDER_LEAD_DAYS = 2;

export function orderDateFor(slotDate: string): Date {
  return addDays(parseDayKey(slotDate.slice(0, 10)), -ORDER_LEAD_DAYS);
}

export function fmtShortDay(k: string): string {
  return parseDayKey(k.slice(0, 10)).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export function fmtPeriod(plan: Pick<Plan, "periodStart" | "periodEnd">): string {
  const a = plan.periodStart ? parseDayKey(plan.periodStart.slice(0, 10)) : null;
  const b = plan.periodEnd ? parseDayKey(plan.periodEnd.slice(0, 10)) : null;
  const f = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (a && b) return `${f(a)} – ${f(b)}`;
  return a ? f(a) : "";
}

/** The next Monday on or after tomorrow, as YYYY-MM-DD: a sensible default start for a new plan. */
export function defaultPlanStart(now: Date = new Date()): string {
  const tomorrow = addDays(new Date(now.getFullYear(), now.getMonth(), now.getDate()), 1);
  const dow = (tomorrow.getDay() + 6) % 7;
  return dayKey(dow === 0 ? tomorrow : addDays(tomorrow, 7 - dow));
}

export function planSlotCounts(plan: Plan): Record<PlanChannel, number> {
  const out = { blog: 0, linkedin: 0, x: 0, facebook: 0, email: 0 } as Record<PlanChannel, number>;
  for (const s of plan.matrix) if (s.status !== "skipped") out[s.channel] += 1;
  return out;
}

/**
 * What an UPDATE slot refreshes, as a short label: "wp12744" for a WordPress post id (a bare number or "wp12744"),
 * otherwise the slug or the address as the planner wrote it. Null when the slot is not an update or names nothing.
 */
export function updateTargetLabel(slot: Pick<PlanSlot, "kind" | "updateOf">): string | null {
  if (slot.kind !== "update" || slot.updateOf === null || slot.updateOf === undefined) return null;
  const s = String(slot.updateOf).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return `wp${s}`;
  if (/^wp\s?-?\d+$/i.test(s)) return `wp${s.replace(/\D+/g, "")}`;
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const path = u.pathname.replace(/\/+$/, "");
      return truncate(`${u.hostname.replace(/^www\./, "")}${path}`, 48);
    } catch {
      return truncate(s, 48);
    }
  }
  return truncate(s, 48);
}

export interface WeekMix {
  /** Monday of the week, YYYY-MM-DD. */
  weekStart: string;
  newCount: number;
  updateCount: number;
  total: number;
}

/** Blog slots per Monday-to-Sunday week, split into new posts and updates. Skipped slots are not counted: nothing will be written for them. */
export function blogMixByWeek(plan: Pick<Plan, "matrix">): Map<string, WeekMix> {
  const out = new Map<string, WeekMix>();
  for (const s of plan.matrix) {
    if (s.channel !== "blog" || s.status === "skipped") continue;
    const k = dayKey(startOfWeek(parseDayKey(s.date.slice(0, 10))));
    const w = out.get(k) ?? { weekStart: k, newCount: 0, updateCount: 0, total: 0 };
    if (s.kind === "update") w.updateCount += 1;
    else w.newCount += 1;
    w.total += 1;
    out.set(k, w);
  }
  return out;
}

/** Weeks whose blog posts outnumber what the policy allows a week. Empty when the policy states no limit. */
export function overLimitWeeks(plan: Pick<Plan, "matrix">, blogsPerWeek: number | null | undefined): WeekMix[] {
  if (blogsPerWeek === null || blogsPerWeek === undefined) return [];
  return Array.from(blogMixByWeek(plan).values())
    .filter((w) => w.total > blogsPerWeek)
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

/** True when the week's new / update split is as close to the policy's share as whole posts allow (a 3-post week at 50% may hold 1 or 2 new posts). */
export function mixMatches(newCount: number, total: number, newShare: number): boolean {
  if (total <= 0) return true;
  const want = total * newShare;
  return newCount >= Math.floor(want + 1e-9) && newCount <= Math.ceil(want - 1e-9);
}

/** "50% new, 50% updates". */
export function describeShare(newShare: number): string {
  const n = Math.round(newShare * 100);
  return `${n}% new, ${100 - n}% updates`;
}

/** A slot as the server wants it back: optional fields that are empty are left out, not sent as null. */
export function serializeSlot(s: PlanSlot): Record<string, unknown> {
  const { raw, ...fields } = s;
  const out: Record<string, unknown> = { ...(raw ?? {}) };
  for (const [k, v] of Object.entries(fields)) {
    if (v === null || v === undefined) continue;
    // Leave a key absent when this screen only defaulted it (an angle the server never had stays absent).
    if ((k === "angle" || k === "targetQuery") && v === "" && raw && raw[k] === undefined) continue;
    out[k] = v;
  }
  return out;
}

/** The plan's matrix with one slot changed (the whole matrix is sent back on every PATCH). */
export function matrixWith(plan: Plan, slotId: string, patch: Partial<PlanSlot>): PlanSlot[] {
  return plan.matrix.map((s) => (s.slotId === slotId ? { ...s, ...patch } : s));
}

/** The slot a calendar entry points at, looked up in the newest plan that contains it. */
export function findSlot(plans: Plan[], slotId: string | null | undefined, planId?: number | string | null): { plan: Plan; slot: PlanSlot } | null {
  if (!slotId) return null;
  if (planId != null) {
    const plan = plans.find((p) => String(p.id) === String(planId));
    const slot = plan?.matrix.find((s) => s.slotId === slotId);
    if (plan && slot) return { plan, slot };
  }
  for (const plan of plans) {
    if (plan.status === "archived") continue;
    const slot = plan.matrix.find((s) => s.slotId === slotId);
    if (slot) return { plan, slot };
  }
  for (const plan of plans) {
    const slot = plan.matrix.find((s) => s.slotId === slotId);
    if (slot) return { plan, slot };
  }
  return null;
}

// ── Tiny persisted flag (a job being waited on) ─────────────────────────────

/** localStorage is a per-viewer convenience: it can be empty, blocked or throw, and the screen must work without it. */
export function usePersistedJson<T>(key: string | null): [T | null, (v: T | null) => void] {
  const read = (): T | null => {
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  };
  const [value, setValue] = useState<T | null>(read);
  useEffect(() => {
    setValue(read());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const set = (v: T | null) => {
    setValue(v);
    if (!key) return;
    try {
      if (v === null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(v));
    } catch {
      /* blocked or full: the waiting banner just won't survive a reload */
    }
  };
  return [value, set];
}

// ── Queries ─────────────────────────────────────────────────────────────────

const base = (wsId: number | null) => ["/api/workspaces", wsId] as const;

/** A GET that answers `null` on 404/405/501 (route not there yet), the parsed JSON on 200, and throws on anything else. */
export async function getOrNull(url: string): Promise<any | null> {
  const res = await fetch(url, { credentials: "include" });
  if (res.status === 404 || res.status === 405 || res.status === 501) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export function usePlans(options: { pollMs?: number | false } = {}) {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const q = useQuery<{ available: boolean; plans: Plan[] }>({
    queryKey: [...base(wsId), "plans"],
    enabled: !!wsId,
    staleTime: 20_000,
    refetchInterval: options.pollMs ?? false,
    queryFn: async () => {
      // /plans is the older marketing-plan list on the server; content plans are listed under /content-plans.
      // The /plans fallback is for servers that answer the contract URL, and normalizePlans drops anything that is not a content plan.
      const primary = await getOrNull(`/api/workspaces/${wsId}/content-plans`);
      if (primary !== null) return { available: true, plans: normalizePlans(primary) };
      const fallback = await getOrNull(`/api/workspaces/${wsId}/plans`);
      const plans = fallback === null ? [] : normalizePlans(fallback);
      // Only legacy marketing plans came back: the content-plan routes are not on this server.
      return plans.length ? { available: true, plans } : { available: false, plans: [] };
    },
  });
  return { plans: q.data?.plans ?? [], available: q.data?.available ?? true, isLoading: q.isLoading, isError: q.isError && !q.data, refetch: q.refetch };
}

export function usePublishPolicy() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const q = useQuery<{ available: boolean; policy: PublishPolicy }>({
    queryKey: [...base(wsId), "publish-policy"],
    enabled: !!wsId,
    staleTime: 60_000,
    queryFn: async () => {
      const json = await getOrNull(`/api/workspaces/${wsId}/publish-policy`);
      return json === null ? { available: false, policy: defaultPolicy() } : { available: true, policy: normalizePolicy(json) };
    },
  });
  // While loading, or when the route is missing, the policy is "unknown": every channel behaves as Manual.
  return { policy: q.data?.available ? q.data.policy : null, available: q.data?.available ?? false, isLoading: q.isLoading, isError: q.isError && !q.data };
}

export function useSavePublishPolicy() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (policy: PublishPolicy) => {
      const body: Record<string, unknown> = {};
      for (const ch of POLICY_CHANNELS) {
        const p = policy[ch];
        const entry: Record<string, unknown> = p.mode === "veto" ? { mode: "veto", vetoHours: p.vetoHours ?? DEFAULT_VETO_HOURS } : { mode: p.mode };
        // The blog's cadence fields travel with the save. A field the server never sent (and the owner never touched) stays out, so a server
        // that predates these fields is not sent a key it would reject.
        if (ch === "blog") {
          if (p.blogsPerWeek !== undefined) entry.blogsPerWeek = p.blogsPerWeek;
          if (p.newUpdateRatio !== undefined) entry.newUpdateRatio = p.newUpdateRatio;
          if (p.requireIndependentQa !== undefined) entry.requireIndependentQa = p.requireIndependentQa;
        }
        body[ch] = entry;
      }
      const c = policy.corrections;
      if (c) {
        // The server merges a policy body field by field, so a date the owner cleared must be sent as null: leaving the key out keeps the stored one.
        body.corrections = {
          mode: c.mode,
          ...(c.mode === "veto" ? { vetoHours: c.vetoHours ?? DEFAULT_VETO_HOURS } : {}),
          manualUntil: c.manualUntil ? policyDayKey(c.manualUntil) : null,
        };
      }
      return (await apiRequest("PUT", `/api/workspaces/${wsId}/publish-policy`, body)).json().catch(() => ({}));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...base(wsId), "publish-policy"] });
      queryClient.invalidateQueries({ queryKey: [...base(wsId), "content"] });
      toast({ title: "Publishing policy saved", description: "It applies to everything the team finishes from now on." });
    },
    onError: (e) => toast({ title: "Couldn't save the publishing policy", description: errorMessage(e, "Please try again."), variant: "destructive" }),
  });
}

export function useCalendar(from: string, to: string) {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const q = useQuery<{ available: boolean; entries: CalendarEntry[] }>({
    queryKey: [...base(wsId), "calendar", from, to],
    enabled: !!wsId,
    staleTime: 20_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const json = await getOrNull(`/api/workspaces/${wsId}/calendar?from=${from}&to=${to}`);
      if (json === null) return { available: false, entries: [] };
      const list = Array.isArray(json) ? json : Array.isArray(json?.entries) ? json.entries : Array.isArray(json?.data) ? json.data : [];
      const entries = (list as any[])
        .filter((e) => e && typeof e === "object" && e.date)
        .map((e): CalendarEntry => ({
          date: String(e.date),
          kind: e.kind === "slot" ? "slot" : "item",
          channel: String(e.channel ?? ""),
          title: String(e.title ?? ""),
          status: String(e.status ?? ""),
          contentItemId: typeof e.contentItemId === "number" ? e.contentItemId : null,
          slotId: e.slotId ?? null,
          planId: e.planId ?? null,
          publishAfter: typeof e.publishAfter === "string" ? e.publishAfter : null,
          vetoState: typeof e.vetoState === "string" ? e.vetoState : null,
          verified: typeof e.verified === "boolean" ? e.verified : null,
          publishedUrl: typeof e.publishedUrl === "string" ? e.publishedUrl : null,
        }));
      // A slot that already has its piece is shown through the piece (it carries the veto / publish / verify state).
      const itemIds = new Set(entries.filter((e) => e.kind === "item" && e.contentItemId != null).map((e) => e.contentItemId));
      return { available: true, entries: entries.filter((e) => !(e.kind === "slot" && e.contentItemId != null && itemIds.has(e.contentItemId))) };
    },
  });
  return { entries: q.data?.entries ?? [], available: q.data?.available ?? true, isLoading: q.isLoading, isError: q.isError && !q.data, refetch: q.refetch };
}

// ── Actions ─────────────────────────────────────────────────────────────────

export function useAgenticActions() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { toast } = useToast();
  const root = `/api/workspaces/${wsId}`;

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: [...base(wsId), "content"] });
    queryClient.invalidateQueries({ queryKey: [...base(wsId), "calendar"] });
    queryClient.invalidateQueries({ queryKey: [...base(wsId), "plans"] });
  };
  const fail = (title: string) => (e: unknown) => toast({ title, description: errorMessage(e, "Please try again."), variant: "destructive" });
  const post = async (path: string, body: unknown = {}) => (await apiRequest("POST", `${root}${path}`, body)).json().catch(() => ({}));

  const hold = useMutation({
    mutationFn: (id: number) => post(`/content/${id}/hold`),
    onSuccess: () => {
      refreshAll();
      toast({ title: "On hold", description: "It will not publish until you release it. It now shows under Needs your review." });
    },
    onError: fail("Couldn't hold it"),
  });

  const release = useMutation({
    mutationFn: (id: number): Promise<{ willAutoPublish?: boolean; note?: string }> => post(`/content/${id}/release`),
    onSuccess: (data) => {
      refreshAll();
      // On a Manual channel the server releases the piece but will not publish it; its note says what to do instead.
      toast({
        title: "Released",
        description: data?.willAutoPublish === false && data?.note ? `Note: ${data.note}.` : "It will go out within a minute or two.",
      });
    },
    onError: fail("Couldn't release it"),
  });

  const rerunGate = useMutation({
    mutationFn: (id: number) => post(`/content/${id}/gate`),
    onSuccess: () => {
      refreshAll();
      toast({ title: "Check re-run", description: "The result is updated below." });
    },
    onError: fail("Couldn't re-run the check"),
  });

  const generatePlan = useMutation({
    mutationFn: ({ weeks, startDate }: { weeks: 2 | 4; startDate: string }): Promise<{ jobId?: string | number }> => post(`/content-plans/generate`, { weeks, startDate }),
    onError: fail("Couldn't start the plan"),
  });

  const requestReview = useMutation({
    mutationFn: (planId: number | string): Promise<{ jobId?: string | number }> => post(`/content-plans/${planId}/request-review`),
    onError: fail("Couldn't ask for a review"),
  });

  const approvePlan = useMutation({
    mutationFn: (planId: number | string) => post(`/content-plans/${planId}/approve`),
    onSuccess: () => {
      refreshAll();
      toast({ title: "Plan approved", description: "Draper will order each piece two days before its date." });
    },
    onError: fail("Couldn't approve the plan"),
  });

  const archivePlan = useMutation({
    mutationFn: (planId: number | string) => post(`/content-plans/${planId}/archive`),
    onSuccess: () => {
      refreshAll();
      toast({ title: "Plan archived" });
    },
    onError: fail("Couldn't archive the plan"),
  });

  const patchPlan = useMutation({
    mutationFn: async ({ planId, matrix, title }: { planId: number | string; matrix?: PlanSlot[]; title?: string }) =>
      (await apiRequest("PATCH", `${root}/content-plans/${planId}`, { ...(matrix ? { matrix: matrix.map(serializeSlot) } : {}), ...(title !== undefined ? { title } : {}) })).json().catch(() => ({})),
    onSuccess: () => refreshAll(),
    onError: (e) => {
      const msg = errorMessage(e, "Please try again.");
      // The server only lets draft and reviewed plans be edited.
      if (/can no longer be edited/i.test(msg)) {
        toast({
          title: "Approved plans can't be changed here",
          description: "The slots of an approved plan are fixed. Archive the plan to stop it, or ask Draper to build a new one.",
          variant: "destructive",
        });
      } else toast({ title: "Couldn't save the change", description: msg, variant: "destructive" });
    },
  });

  return { hold, release, rerunGate, generatePlan, requestReview, approvePlan, archivePlan, patchPlan };
}

/** Release, plan approval, archiving and the policy are owner / admin actions on the server. Unknown role (could not be read) = let the server decide. */
export function useIsAdmin(): boolean {
  const { canPublishLive } = useWorkspaceRole();
  return canPublishLive !== false;
}
